import apiClient from './apiClient';
import { uploadAssetDirect } from './directUpload';
import type { AccountInfo, StaticAssetInfo, StaticPage, StaticPageAdmin, StaticPageWrite, Uuid } from '../types';

const directApiUrl = '/direct/usersvc';

const accountApi = {
  getAccountInfo: () => apiClient.get<AccountInfo>(`${directApiUrl}/accounts/info`),

  getStaticPages: () => apiClient.get<StaticPage[]>(`${directApiUrl}/static_pages`),

  // Admin variant: all=1 asks the backend for unlisted (both-placement-off)
  // pages too; requires an account-admin token.
  getStaticPagesAdmin: () =>
    apiClient.get<StaticPage[]>(`${directApiUrl}/static_pages`, { params: { all: 1 } }),

  getStaticPage: (id: Uuid) => apiClient.get<StaticPage>(`${directApiUrl}/static_pages/${id}`),

  getStaticPageBySlug: (slug: string) =>
    apiClient.get<StaticPage>(`${directApiUrl}/static_pages/by_slug/${slug}`),

  getStaticPageHomepage: () => apiClient.get<StaticPage>(`${directApiUrl}/static_pages/homepage`),

  // `raw` goes through `options.params` — the preferred way to pass scalar
  // query params (apiClient merges them with its own `locale` param).
  getStaticPageRaw: (id: Uuid) =>
    apiClient.get<StaticPageAdmin>(`${directApiUrl}/static_pages/${id}`, { params: { raw: 1 } }),

  createStaticPage: (payload: StaticPageWrite) =>
    apiClient.post<StaticPageAdmin>(`${directApiUrl}/static_pages`, { static_page: payload }),

  updateStaticPage: (id: Uuid, payload: Partial<StaticPageWrite>) =>
    apiClient.patch<StaticPageAdmin>(`${directApiUrl}/static_pages/${id}`, { static_page: payload }),

  deleteStaticPage: (id: Uuid) => apiClient.delete(`${directApiUrl}/static_pages/${id}`),

  reorderStaticPages: (ids: Uuid[]) =>
    apiClient.patch<StaticPage[]>(`${directApiUrl}/static_pages/reorder`, { ids }),

  getStaticAssets: () => apiClient.get<StaticAssetInfo[]>(`${directApiUrl}/static_assets`),

  // `force` must only ever be sent as `force=1` (never `force=0`) — the
  // backend treats ANY non-blank `params[:force]` (including "0"/"false")
  // as a force-delete, so the param is omitted entirely rather than sent
  // false. Uses `options.params` rather than string-concatenating onto the
  // path, same reasoning as getStaticPageRaw above.
  deleteStaticAsset: (id: Uuid, force = false) =>
    apiClient.delete(`${directApiUrl}/static_assets/${id}`, undefined, {
      params: force ? { force: 1 } : undefined,
    }),
};

export default accountApi;

export const uploadStaticAsset = (file: File): Promise<StaticAssetInfo> =>
  uploadAssetDirect<StaticAssetInfo>(directApiUrl, 'static_assets', file);
